import { useState, type ReactNode } from 'react';
import styles from './VideoDetailInfo.module.css';
import { TextPart, Video, UseVideoDetailInfoReturn } from './types';
import { useIsMobile } from '../../config/breakpoints';

/**
 * VideoDetailInfoコンポーネントのロジックを分離したカスタムフック
 */
export const useVideoDetailInfo = (
  video: Video,
  handleTagClick: (tag: string) => void,
  likesCount?: number
): UseVideoDetailInfoReturn => {
  // 長い説明文は初期状態では折りたたみ、ボタンで展開できるようにする
  const [expanded, setExpanded] = useState(false);
  // タイトル用の展開状態も追加
  const [titleExpanded, setTitleExpanded] = useState(false);
  
  // SP版用のレスポンシブ（650px以下で SP判定）
  const isSP = useIsMobile();
  
  const maxLength = isSP ? 100 : 300; // SP版では100文字、PC版では300文字以上なら折りたたみ表示
  const maxTitleLength = 50; // タイトルは50文字以上なら折りたたみ表示
  
  const description = video.description || '';
  const title = video.title || '無題の動画';
  
  const isLong = description.length > maxLength;
  const isTitleLong = title.length > maxTitleLength;
  
  const displayedDescription =
    expanded || !isLong ? description : description.substring(0, maxLength) + '...';
  const displayedTitle =
    titleExpanded || !isTitleLong ? title : title.substring(0, maxTitleLength) + '...';

  // URLを検出してリンクに変換する関数
  const convertUrlsToLinks = (text?: string): ReactNode[] => {
    if (!text) return [];
    
    // URLを検出する正規表現（終端をより正確に判定する改良版）
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    // テキストをURLで分割
    const parts: TextPart[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    
    // 正規表現で検索しながらテキストを分割
    while ((match = urlRegex.exec(text)) !== null) {
      // URLの前の通常テキスト
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.substring(lastIndex, match.index)
        });
      }
      
      // URL部分
      parts.push({
        type: 'url',
        content: match[0]
      });
      
      lastIndex = match.index + match[0].length;
    }
    
    // 最後の部分を追加
    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        content: text.substring(lastIndex)
      });
    }
    
    // パーツを適切なReact要素に変換
    return parts.map((part, index) => {
      if (part.type === 'text') {
        return <span key={`text-${index}`}>{part.content}</span>;
      } 
      return (
        <a
          key={`link-${index}`}
          href={part.content}
          target="_blank"
          rel="noopener noreferrer"
          className={styles['VideoDetailInfo-descriptionLink']}
        >
          {part.content}
        </a>
      );
    });
  };

  // 作成日時や再生数の表示用ヘルパー
  const getRelativeTime = (dateStr: string): string => {
    const createdDate = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - createdDate.getTime();
    const diffYears = diff / (1000 * 60 * 60 * 24 * 365);
    if (diffYears >= 1) {
      return Math.floor(diffYears) + ' 年前';
    }
    const diffMonths = diff / (1000 * 60 * 60 * 24 * 30);
    if (diffMonths >= 1) {
      return Math.floor(diffMonths) + ' ヶ月前';
    }
    const diffDays = diff / (1000 * 60 * 60 * 24);
    if (diffDays >= 1) {
      return Math.floor(diffDays) + ' 日前';
    }
    return '今日';
  };

  const formatViewCount = (count: number): string => {
    if (count >= 10000) {
      return Math.floor(count / 10000) + '万';
    }
    return count.toString();
  };

  const viewCountDisplay = `${formatViewCount(video.view_count)} 回視聴  ${getRelativeTime(video.created_at)}`;

  const relativeTime = getRelativeTime(video.created_at);
  const formattedViewCount = `${formatViewCount(video.view_count)} views`;
  const formattedLikesCount = likesCount !== undefined ? `${formatViewCount(likesCount)} likes` : '';

  // TODO: 手術日入力UIが実装されたら ?? new Date() を削除すること
  const formattedSurgeryDate = new Date(video.surgery_date ?? new Date()).toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  // ボタン押下時に診療科の検索を実施
  const handleSpecialtyButtonClick = (): void => {
    if (video.specialty && video.specialty.name) {
      handleTagClick(`診療科:${video.specialty.name}`);
    }
  };

  return {
    expanded,
    setExpanded,
    titleExpanded,
    setTitleExpanded,
    isSP,
    displayedDescription,
    displayedTitle,
    isLong,
    isTitleLong,
    viewCountDisplay,
    relativeTime,
    formattedSurgeryDate,
    formattedViewCount,
    formattedLikesCount,
    convertUrlsToLinks,
    handleSpecialtyButtonClick
  };
}; 
